import { useEffect, useReducer, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, PageLoading } from "../index.js";
import { useApiAction } from "../../hooks/useApi.js";
import { PRODUCT_TYPE_LABELS, STATUS_LABELS, createListOps, formatDateOnly, formatTotal, listInitial, listQueryString, listReduce, listView } from "../../lib/productOrders.js";

/**
 * The filtered, paged list of product orders.
 *
 *   mode "admin"    the Orders screen: open requests by default, with
 *                   Awaiting quote / Ready for billing / Invoiced / Cancelled /
 *                   All, a client filter and a search box
 *   mode "billing"  Delma's tab on the billing desk (Task 6): Ready for
 *                   billing / Invoiced / All history, a QuickBooks # column,
 *                   no New order; filters and their setter come from the
 *                   caller, which keeps them under the page's own URL keys
 *
 * Filters live in the page's URL (status, clientId, search, page) so a reload
 * or a shared link lands on the same view. The API query is built from those
 * four keys ONLY — listQueryString — never from the whole location.search,
 * which the Shell may fill with view-as and date-range keys for other pages.
 * Changing any filter drops the page back to 1.
 *
 * WHAT IS SHOWN IS BOUND TO THE QUERY THE CONTROLS SHOW. The rows, the ready
 * total and the page position are only true of the query that produced them,
 * so the state is a reducer (lib/productOrders.js: listInitial / listReduce)
 * that keeps a result with its query and its request number, driven by
 * createListOps from the same file with the API injected; this component only
 * wires those to React and renders listView(state, query). The moment a
 * client, status, search or page changes, the previous result is gone — the
 * controls stay where they are (focus and typed text survive) and the body
 * says "Loading orders…" until THIS query's reply arrives. A reply for an
 * earlier query, or for an earlier visit to this same query (A→B→A), changes
 * nothing whenever it lands; a failed load shows its error and no rows. The
 * shared useApi hook is deliberately not used here: it keeps the previous
 * path's data across a path change.
 *
 * A write elsewhere on the page (refreshKey) silently reloads the SAME query:
 * its rows stay, marked as updating, until the fresh reply replaces them. A
 * query change is always a plain load, whatever refreshKey has been.
 *
 * The reply is validated before a single row is rendered — its shape, and
 * that it answers this query (page, client, status). One that does not is an
 * error on screen, not an empty list and not another view's rows.
 */
const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "awaiting_quote", label: "Awaiting quote" },
  { key: "ready_for_billing", label: "Ready for billing" },
  { key: "invoiced", label: "Invoiced" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];
const BILLING_TABS = [
  { key: "ready_for_billing", label: "Ready for billing" },
  { key: "invoiced", label: "Invoiced" },
  { key: "all", label: "All history" },
];
const CHIP = {
  awaiting_quote: { bg: "#fbeae0", fg: "#8a4b1a" },
  ready_for_billing: { bg: "#eff6ff", fg: "#2563eb" },
  invoiced: { bg: "#f0fdf4", fg: "#16a34a" },
  cancelled: { bg: "#f5f5f5", fg: "#a3a3a3" },
};

export function StatusChip({ status }) {
  const c = CHIP[status] ?? CHIP.cancelled;
  return <span className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium whitespace-nowrap" style={{ backgroundColor: c.bg, color: c.fg }}>{STATUS_LABELS[status] ?? status}</span>;
}

/** The four list filters, read from the URL with the API's own defaults. */
export function useOrderListFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    status: params.get("status") || "open",
    clientId: params.get("clientId") || "",
    search: params.get("search") || "",
    page: Math.max(1, Number.parseInt(params.get("page") || "1", 10) || 1),
  };
  const setFilter = (key, value) => setParams((p) => {
    if (value) p.set(key, value); else p.delete(key);
    if (key !== "page") p.delete("page");
    return p;
  }, { replace: true });
  return [filters, setFilter];
}

export function ProductOrderList({ mode = "admin", onSelect, onCreate, selectedId = null, clients = [], refreshKey = 0, filters: given, setFilter: setGiven }) {
  const [urlFilters, setUrlFilter] = useOrderListFilters();
  const filters = given ?? urlFilters;
  const setFilter = setGiven ?? setUrlFilter;
  const qs = listQueryString(filters);
  const api = useApiAction();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [state, dispatch] = useReducer(listReduce, qs, listInitial);
  // One ops for the life of this list: its request counter must never restart
  // under a state that has already counted higher.
  const [ops] = useState(() => createListOps({ api: (path) => apiRef.current(path), dispatch }));

  // The one place a load starts: the query changed (a plain load), or a write
  // elsewhere on the page asked for a refresh of the same query (silent).
  useEffect(() => { ops.sync(qs, refreshKey); }, [qs, refreshKey, ops]);
  useEffect(() => () => ops.dispose(), [ops]);

  // Judged against the query the controls show NOW — in the render before the
  // effect above has run, the state still holds the previous query's result.
  const view = listView(state, qs);

  return (
    <Card className="p-0">
      <div className="px-4 py-2.5 border-b border-border-light flex flex-wrap items-center gap-2">
        {(mode === "billing" ? BILLING_TABS : STATUS_TABS).map((t) => (
          <button key={t.key} type="button" onClick={() => setFilter("status", t.key === "open" ? "" : t.key)} aria-pressed={filters.status === t.key}
            className={`px-3 py-1 rounded-full text-[12px] font-medium border ${filters.status === t.key ? "bg-accent text-white border-accent" : "border-border-default text-text-secondary hover:text-text-primary"}`}>
            {t.label}
          </button>
        ))}
        <span className="flex-1" />
        <label className="sr-only" htmlFor="product-order-client-filter">Client</label>
        <select id="product-order-client-filter" value={filters.clientId} onChange={(e) => setFilter("clientId", e.target.value)} className="text-[12px] border border-border-default rounded px-2 py-1 bg-surface">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="sr-only" htmlFor="product-order-search">Search orders</label>
        <input id="product-order-search" type="search" defaultValue={filters.search} placeholder="Search description, client, invoice #" maxLength={200}
          onKeyDown={(e) => { if (e.key === "Enter") setFilter("search", e.currentTarget.value.trim()); }}
          onBlur={(e) => { if (e.currentTarget.value.trim() !== filters.search) setFilter("search", e.currentTarget.value.trim()); }}
          className="text-[12px] border border-border-default rounded px-2 py-1 bg-surface w-56" />
        {mode === "admin" && onCreate && (
          <button type="button" onClick={onCreate} className="px-3 py-1 rounded-card bg-accent text-white text-[12px] font-medium">New order</button>
        )}
      </div>

      {/* The body is the only part that depends on a reply. Its attributes name the phase, the query it is shown for and the latest request made. */}
      <div data-product-order-list={mode} data-list-phase={view.phase} data-list-query={qs} data-list-request={state.request} aria-busy={view.phase === "loading" || view.refreshing === true || undefined}>
        {view.phase === "ready" ? (
          <ListResult list={view.list} refreshing={view.refreshing} mode={mode} selectedId={selectedId} onSelect={onSelect} setFilter={setFilter} />
        ) : view.phase === "error" ? (
          <div role="alert" className="px-5 py-10 text-center">
            <div className="text-[13px] font-semibold text-text-primary mb-1">Couldn't load the orders</div>
            <div className="text-[12px] text-text-secondary mb-3">{view.error?.message || "An unexpected error occurred."}</div>
            <button type="button" onClick={() => ops.retry()} className="text-[12px] text-accent hover:underline font-medium">Try again</button>
          </div>
        ) : (
          <div role="status"><PageLoading label="Loading orders…" /></div>
        )}
      </div>
    </Card>
  );
}

/** One query's result — the ready summary, the rows, the page position. Rendered only under the controls that asked for it. */
function ListResult({ list, refreshing, mode, selectedId, onSelect, setFilter }) {
  const { items, total, page, pageSize, summary } = list;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + items.length;

  return (
    <>
      <div className="px-4 py-2 text-[12px] text-text-secondary border-b border-border-light">
        <span className="font-medium text-text-primary">Ready for billing — matching client/search:</span> {summary.readyCount} order{summary.readyCount === 1 ? "" : "s"}, {formatTotal(summary.readyTotal)} approved
        {refreshing && <span className="text-text-muted" data-list-refreshing> · updating…</span>}
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-10 text-center text-[13px] text-text-muted">No orders match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase text-text-muted" style={{ letterSpacing: "0.5px" }}>
                {["Client", "Product", "Description", "Qty", "Requested delivery", "Status", "Approved total", ...(mode === "billing" ? ["QB #"] : [])].map((h) => <th key={h} scope="col" className="text-left font-medium px-4 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className={`border-t border-border-light ${selectedId === o.id ? "bg-accent-bg" : ""}`}>
                  <td className="px-4 py-2 font-medium text-text-primary">{o.client.name}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{PRODUCT_TYPE_LABELS[o.productType]}</td>
                  <td className="px-4 py-2 max-w-[360px]">
                    <button type="button" onClick={() => onSelect?.(o.id)} className="text-left text-accent hover:underline line-clamp-2 whitespace-pre-line break-words" aria-current={selectedId === o.id ? "true" : undefined}>
                      {o.description}
                    </button>
                  </td>
                  <td className="px-4 py-2 tabular-nums">{o.quantity.toLocaleString()}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatDateOnly(o.requestedDeliveryDate)}</td>
                  <td className="px-4 py-2"><StatusChip status={o.status} /></td>
                  <td className="px-4 py-2 whitespace-nowrap tabular-nums">{formatTotal(o.approvedTotal)}</td>
                  {mode === "billing" && <td className="px-4 py-2 font-mono text-[12px] whitespace-nowrap">{o.qbInvoiceNumber ?? "—"}{o.invoiceDate ? <span className="text-text-muted font-sans"> · {formatDateOnly(o.invoiceDate)}</span> : null}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="px-4 py-2 flex items-center justify-between text-[12px] text-text-muted border-t border-border-light">
          <span>Showing {from}–{to} of {total}</span>
          <span className="flex items-center gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setFilter("page", String(page - 1))} className="px-2 py-1 rounded border border-border-default disabled:opacity-40">Prev</button>
            <button type="button" disabled={page * pageSize >= total} onClick={() => setFilter("page", String(page + 1))} className="px-2 py-1 rounded border border-border-default disabled:opacity-40">Next</button>
          </span>
        </div>
      )}
    </>
  );
}
